//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { FeatureNames } from '@bvengineering/curalate-features';
import { IUrlRouterProvider } from 'angular-ui-router';

import {
  MODERATOR_ALL_SHARED_MEDIA_TITLE,
  MODERATOR_STREAM_TITLE,
  mediaLibraryQueryKeys,
} from '@media-library/constants/mediaLibraryConstants';
import { IContentDrawerSettingsService } from '@redux/content-drawer/ContentDrawerSettingsService';
import { ContentDrawerSidebarOptions } from '@redux/content-drawer/ContentDrawerSidebarOptions';

import {
  NavGroup,
  StateFactory,
  componentNameToTemplate,
  hasAllFeatures,
  hasFeature,
  hasSomeFeatures,
  lazyLoadModule,
} from 'app/initialization/routing/stateHelpers';
import { SessionMode } from 'app/moderation-mode/constants/SessionMode';
import { resolveSessionMode } from 'app/moderation-mode/moderationModeResolver';

const canAccessModeratorView = () =>
  resolveSessionMode() === SessionMode.Moderator;

export function routingRules(urlRouterProvider: IUrlRouterProvider) {
  urlRouterProvider.when('/library', '/library/team-media');
  urlRouterProvider.when(
    '/library/collections/{id}',
    '/library/collections/{id}/content',
  );
}

export const states = {
  library: StateFactory({
    url: '/library',
    template: componentNameToTemplate('libraryPage'),
    data: {
      title: 'Library',
      navGroup: NavGroup.MEDIA,
      hasAccess: () => hasFeature(FeatureNames.MediaLibraryPage),
    },
    resolve: {
      ...lazyLoadModule(
        'curalate.library',
        () => import('app/library/library.app'),
      ),
      // We include this in the resolve block because we need to make sure
      // the content drawer settings are applied before we transition into
      // the library state. This is a special case because the library and
      // the content-drawer compete for control over global filters, so it
      // is critical to preserve the expected order: hide drawer, init lib
      setContentDrawerSettings: [
        'ContentDrawerSettingsService',
        (ContentDrawerSettingsService: IContentDrawerSettingsService) => {
          return new Promise<void>((resolve) => {
            ContentDrawerSettingsService.setPageSettings(
              ContentDrawerSidebarOptions.Library,
            );
            resolve();
          });
        },
      ],
    },
  }),

  'library.team-media': StateFactory({
    url: `/team-media?${mediaLibraryQueryKeys}`,
    data: {
      title: 'Library',
      navGroup: NavGroup.MEDIA,
      hasAccess: () =>
        hasFeature(FeatureNames.MediaLibrarySourcedOrganicContent),
    },
    views: {
      main: {
        template: componentNameToTemplate('mediaLibraryPage'),
      },
    },
    resolve: {
      ...lazyLoadModule(
        'curalate',
        () => import('@media-library/page/MediaLibraryPage'),
      ),
    },
  }),

  'library.my-media': StateFactory({
    url: `/my-media?${mediaLibraryQueryKeys}`,
    data: {
      title: 'Library',
      navGroup: NavGroup.MEDIA,
      hasAccess: () =>
        hasFeature(FeatureNames.MediaLibrarySourcedOrganicContent),
    },
    views: {
      main: {
        template: componentNameToTemplate('mediaLibraryPage'),
      },
    },
    resolve: {
      ...lazyLoadModule(
        'curalate',
        () => import('@media-library/page/MediaLibraryPage'),
      ),
    },
  }),

  'library.all-media': StateFactory({
    url: `/all-media?${mediaLibraryQueryKeys}`,
    data: {
      title: 'Library',
      navGroup: NavGroup.MEDIA,
      hasAccess: () => hasFeature(FeatureNames.MediaLibraryPage),
    },
    views: {
      main: {
        template: componentNameToTemplate('mediaLibraryPage'),
      },
    },
    resolve: {
      ...lazyLoadModule(
        'curalate',
        () => import('@media-library/page/MediaLibraryPage'),
      ),
    },
  }),

  'library.tracked-sources': StateFactory({
    url: '/tracked-sources',
    redirectTo: hasSomeFeatures(
      FeatureNames.HarmonyAiCreatorsIntegration,
      FeatureNames.InfluencersDiscovery,
    )
      ? 'library.tracked-sources.user'
      : 'library.tracked-sources.hashtag',
    data: {
      title: 'Library',
      navGroup: NavGroup.MEDIA,
      hasAccess: () =>
        hasAllFeatures(
          FeatureNames.MediaLibraryPage,
          FeatureNames.MediaLibrarySourcedOrganicContent,
        ),
    },
    views: {
      main: {
        template: componentNameToTemplate('trackedSourcesPage'),
      },
    },
    resolve: {
      ...lazyLoadModule(
        'curalate',
        () => import('@tracked-sources/page/TrackedSourcesPage'),
      ),
    },
  }),

  'library.tracked-sources.user': StateFactory({
    url: '/users',
    data: {
      title: 'Tracked Sources',
      navGroup: NavGroup.MEDIA,
      hasAccess: () =>
        hasAllFeatures(
          FeatureNames.MediaLibraryPage,
          FeatureNames.MediaLibrarySourcedOrganicContent,
          FeatureNames.InfluencersDiscovery,
        ),
    },
    views: {
      main: {
        template: componentNameToTemplate('trackedSourcesPage'),
      },
    },
    resolve: {
      ...lazyLoadModule(
        'curalate',
        () => import('@tracked-sources/page/TrackedSourcesPage'),
      ),
    },
  }),

  'library.hashtags': StateFactory({
    url: '/hashtags-management',
    data: {
      title: 'Hashtags',
      navGroup: NavGroup.MEDIA,
      hasAccess: () =>
        hasAllFeatures(
          FeatureNames.HashtagTrackingAndInsights,
          FeatureNames.MediaLibraryPage,
          FeatureNames.MediaLibrarySourcedOrganicContent,
          /* TODO: PD-271441: Remove this once MultiHandleHashtagTracking feature flag is released */
          FeatureNames.MultiHandleHashtagTracking,
        ),
    },
    views: {
      main: {
        template: componentNameToTemplate('hashtagsManagementPage'),
      },
    },
    resolve: {
      ...lazyLoadModule(
        'curalate',
        () => import('@hashtags/page/HashtagsManagementPage'),
      ),
    },
  }),

  // TODO: PD-271441: Remove this once MultiHandleHashtagTracking feature flag is released
  'library.tracked-sources.hashtag': StateFactory({
    url: '/hashtags',
    data: {
      title: 'Tracked Sources',
      navGroup: NavGroup.MEDIA,
      hasAccess: () =>
        hasAllFeatures(
          FeatureNames.HashtagTrackingAndInsights,
          FeatureNames.MediaLibraryPage,
          FeatureNames.MediaLibrarySourcedOrganicContent,
        ),
    },
    views: {
      main: {
        template: componentNameToTemplate('trackedSourcesPage'),
      },
    },
    resolve: {
      ...lazyLoadModule(
        'curalate',
        () => import('@tracked-sources/page/TrackedSourcesPage'),
      ),
    },
  }),

  'library.collections': StateFactory({
    url: '/collections/:collectionId',
    data: {
      title: 'Library',
      navGroup: NavGroup.MEDIA,
    },
    views: {
      main: {
        template: componentNameToTemplate('collectionsPage'),
      },
    },
    resolve: {
      ...lazyLoadModule(
        'curalate',
        () => import('@collections/page/CollectionsPage'),
      ),
    },
  }),

  'library.collections.content': StateFactory({
    url: '/content',
    data: {
      title: 'Library',
      navGroup: NavGroup.MEDIA,
    },
    views: {
      main: {
        template: componentNameToTemplate('collectionsPage'),
      },
    },
    resolve: {
      ...lazyLoadModule(
        'curalate',
        () => import('@collections/page/CollectionsPage'),
      ),
    },
  }),
  'library.distribute': StateFactory({
    url: '/moderation/distribute',
    data: {
      title: 'Distribute',
      navGroup: NavGroup.MEDIA,
      hasAccess: () =>
        hasAllFeatures(
          FeatureNames.AiContentModeration,
          FeatureNames.AiContentModerationBackend,
        ),
    },
    views: {
      main: {
        template: componentNameToTemplate('readyToDistributePage'),
      },
    },
    resolve: {
      ...lazyLoadModule(
        'curalate',
        () =>
          import(
            '@media-library/ai-content-curation/page/ReadyToDistributePage'
          ),
      ),
    },
  }),
  'library.needs-action': StateFactory({
    url: '/moderation/needs-action',
    data: {
      title: 'Needs Action',
      navGroup: NavGroup.MEDIA,

      hasAccess: () =>
        hasAllFeatures(
          FeatureNames.AiContentModeration,
          FeatureNames.AiContentModerationBackend,
          FeatureNames.MediaLibrarySourcedOrganicContent,
        ),
    },
    views: {
      main: {
        template: componentNameToTemplate('needsActionPage'),
      },
    },
    resolve: {
      ...lazyLoadModule(
        'curalate',
        () => import('@media-library/ai-content-curation/page/NeedsActionPage'),
      ),
    },
  }),
  'library.rejected': StateFactory({
    url: '/moderation/rejected',
    data: {
      title: 'Rejected',
      navGroup: NavGroup.MEDIA,
      hasAccess: () =>
        hasAllFeatures(
          FeatureNames.AiContentModeration,
          FeatureNames.AiContentModerationBackend,
        ),
    },
    views: {
      main: {
        template: componentNameToTemplate('rejectedPage'),
      },
    },
    resolve: {
      ...lazyLoadModule(
        'curalate',
        () => import('@media-library/ai-content-curation/page/RejectedPage'),
      ),
    },
  }),

  'library.moderation-all-shared-media': StateFactory({
    url: `/moderation/all-shared-media?${mediaLibraryQueryKeys}`,
    data: {
      title: MODERATOR_ALL_SHARED_MEDIA_TITLE,
      navGroup: NavGroup.MEDIA,
      hasAccess: canAccessModeratorView,
    },
    views: {
      main: {
        template: componentNameToTemplate('mediaLibraryPage'),
      },
    },
    resolve: {
      ...lazyLoadModule(
        'curalate',
        () => import('@media-library/page/MediaLibraryPage'),
      ),
    },
  }),
  'library.moderation-stream': StateFactory({
    url: `/moderation/streams?${mediaLibraryQueryKeys}`,
    data: {
      title: MODERATOR_STREAM_TITLE,
      navGroup: NavGroup.MEDIA,
      hasAccess: canAccessModeratorView,
    },
    views: {
      main: {
        template: componentNameToTemplate('mediaLibraryPage'),
      },
    },
    resolve: {
      ...lazyLoadModule(
        'curalate',
        () => import('@media-library/page/MediaLibraryPage'),
      ),
    },
  }),
};
